import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { getRegs, notifyByEmail, getProdCoordenadorPfId, PROD_COORDENADOR_PF_FIELDS } from '../../../utils/fc/index.js';
import oikosApi from '../../../services/oikos/index.js';
import { notifyError, notifySuccess } from '../../../utils/notify';
import ModalLiberacaoFaturamentoRec from '../../Template/Doc/ModalLiberacaoFaturamentoRec';
import { isRecAutorizado, insertProdStatusFaturamentoAutorizado } from '../../../utils/recLiberacaoFaturamento';

const triggerSx = {
  fontSize: 11,
  textTransform: 'none',
  lineHeight: 1.2,
  maxWidth: 96,
  whiteSpace: 'normal',
  p: '2px 4px',
};

const normalizeProdIds = (prodIds, fallbackProdId) => {
  const ids = Array.from(new Set((prodIds || []).map((x) => Number(x)).filter(Boolean)));
  if (ids.length > 0) return ids;
  const fallback = Number(fallbackProdId);
  return fallback ? [fallback] : [];
};

const ModalFormAutorizarFaturamento = ({ produto, userOikosId, setCount, onProductTimelineChanged, orcRubr_id, projectProducts, ...props }) => {
  const [libModalOpen, setLibModalOpen] = useState(false);
  const [libModalRec, setLibModalRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [selectedProdIds, setSelectedProdIds] = useState([]);
  const pendingProdIdsRef = useRef([]);

  const recId = produto?.rec_id;

  const resolveProduct = async (prodId) => {
    const id = Number(prodId);
    const cached = (projectProducts || []).find((p) => Number(p.prod_id) === id);
    if (cached?.prod_id && getProdCoordenadorPfId(cached)) return cached;

    const raw = await getRegs('prod', { prod_id: id, prod_ativo: 1 }, PROD_COORDENADOR_PF_FIELDS);
    const row = Array.isArray(raw) && raw[0] ? raw[0] : null;
    if (!row?.prod_id) {
      throw new Error(`Produto #${id} não encontrado.`);
    }
    if (!getProdCoordenadorPfId(row)) {
      throw new Error(`Responsável não definido para o produto #${id}.`);
    }
    return row;
  };

  const insertStatus8For = async (p) => {
    await insertProdStatusFaturamentoAutorizado({
      prod_id: p.prod_id,
      pf_id: getProdCoordenadorPfId(p) || getProdCoordenadorPfId(produto),
      prodStatusAlt_obs: p.prodStatusAlt_obs || '',
      userOikosId,
    });
  };

  const finishFaturamentoProdutos = async (recRow, prodIds) => {
    const dtFat = recRow?.rec_dtLibAutNf
      ? moment(recRow.rec_dtLibAutNf).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD');

    const finalIds = normalizeProdIds(prodIds, produto?.prod_id);

    const vlrLiberado =
      recRow?.rec_vlrLibAutNf != null && recRow.rec_vlrLibAutNf !== ''
        ? recRow.rec_vlrLibAutNf
        : recRow?.rec_vlrBru;

    for (const id of finalIds) {
      const p = await resolveProduct(id);

      await oikosApi.post('/', {
        action: 'update',
        table: 'prod',
        data: {
          prod_id: p.prod_id,
          prod_dtAutFat: dtFat,
        },
      });

      await insertStatus8For(p);
      await notifyByEmail(p.prod_id, {
        obs: p.prodStatusAlt_obs || '',
        vlrLiberado,
        dtAutFat: dtFat,
      });
    }
  };

  const refreshUi = () => {
    if (setCount) setCount((prev) => prev + 1);
    if (onProductTimelineChanged) onProductTimelineChanged();
  };

  const completeAutorizacao = async (recRow, prodIds) => {
    await finishFaturamentoProdutos(recRow, prodIds);
    notifySuccess('Faturamento autorizado no(s) produto(s).');
    refreshUi();
  };

  const openAutorizacao = async (prodIds) => {
    const ids = normalizeProdIds(prodIds, produto?.prod_id);
    pendingProdIdsRef.current = ids;

    if (!recId) {
      notifyError(
        'Associe o produto a um evento de pagamento na grelha ou refaça a aprovação do cliente com vínculo.'
      );
      return;
    }
    setLoading(true);
    try {
      const raw = await getRegs('rec', { rec_id: recId });
      const recRow = Array.isArray(raw) && raw[0] ? raw[0] : null;
      if (!recRow) {
        notifyError('Evento de pagamento não encontrado.');
        return;
      }
      if (isRecAutorizado(recRow)) {
        await completeAutorizacao(recRow, ids);
        return;
      }
      setLibModalRec(recRow);
      setLibModalOpen(true);
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha ao abrir autorização de faturamento.');
    } finally {
      setLoading(false);
    }
  };

  const onLiberacaoSuccess = async () => {
    const rid = libModalRec?.rec_id;
    const prodIds = [...pendingProdIdsRef.current];
    setLibModalOpen(false);
    setLibModalRec(null);
    if (!rid) return;
    setLoading(true);
    try {
      const raw = await getRegs('rec', { rec_id: rid });
      const row = Array.isArray(raw) && raw[0] ? raw[0] : null;
      if (!row) {
        notifyError('Não foi possível recarregar o evento de pagamento.');
        return;
      }
      await completeAutorizacao(row, prodIds);
    } catch (e) {
      console.log(e);
      notifyError(e?.message || 'Falha ao finalizar autorização.');
    } finally {
      setLoading(false);
    }
  };

  const eligibleProducts = (projectProducts || [])
    .filter((p) => Number(p?.rec_id) === Number(recId))
    .filter((p) => Number(p?.prod_id) !== 0);

  const openFlow = () => {
    if (!recId) {
      openAutorizacao([produto?.prod_id]);
      return;
    }

    if (eligibleProducts.length <= 1) {
      const only = eligibleProducts[0]?.prod_id ? [eligibleProducts[0].prod_id] : [produto?.prod_id];
      setSelectedProdIds(only);
      openAutorizacao(only);
      return;
    }

    const ensureCurrent = Number(produto?.prod_id);
    setSelectedProdIds(ensureCurrent ? [ensureCurrent] : []);
    setSelectOpen(true);
  };

  const confirmSelection = () => {
    const ids = normalizeProdIds(selectedProdIds, produto?.prod_id);
    if (ids.length === 0) {
      notifyError('Selecione pelo menos um produto para autorizar.');
      return;
    }
    setSelectOpen(false);
    openAutorizacao(ids);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        sx={triggerSx}
        disabled={loading}
        onClick={openFlow}
      >
        Autorizar faturamento
      </Button>

      <Dialog open={selectOpen} onClose={() => (loading ? null : setSelectOpen(false))} fullWidth maxWidth="sm">
        <DialogTitle>Autorizar faturamento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Selecione quais produtos deste projeto (vinculados ao mesmo evento de pagamento) também terão o faturamento
            autorizado nesta mesma ação.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={eligibleProducts}
              value={eligibleProducts.filter((p) => (selectedProdIds || []).includes(Number(p.prod_id)))}
              getOptionLabel={(opt) => opt?.prod_desc ? String(opt.prod_desc) : `Produto #${opt?.prod_id}`}
              onChange={(_, values) => {
                const ids = (values || []).map((v) => Number(v?.prod_id)).filter(Boolean);
                setSelectedProdIds(ids);
              }}
              renderOption={(optionProps, option, { selected }) => (
                <li {...optionProps} key={option.prod_id}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={option?.prod_desc || ''}>
                      {option?.prod_desc || `Produto #${option?.prod_id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option?.prod_id}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produtos a autorizar"
                  placeholder="Selecione um ou mais produtos"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={confirmSelection} disabled={loading}>
            Continuar
          </Button>
        </DialogActions>
      </Dialog>

      <ModalLiberacaoFaturamentoRec
        open={libModalOpen}
        rec={libModalRec}
        onClose={() => {
          if (loading) return;
          setLibModalOpen(false);
          setLibModalRec(null);
        }}
        onSuccess={onLiberacaoSuccess}
      />
    </>
  );
};

ModalFormAutorizarFaturamento.propTypes = {
  produto: PropTypes.object,
  userOikosId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setCount: PropTypes.func,
  onProductTimelineChanged: PropTypes.func,
  projectProducts: PropTypes.array,
};

export default ModalFormAutorizarFaturamento;
